<template>
  <div class="flex flex-row flex-wrap">
    <HeadTitle
      title="Detalles de usuario"
      description="Vista de visualización y edición de un usuario del sistema"
    />
    <EditRecordButton @update:is-edit-mode="toggleEditMode" />
  </div>
  <br />
  <form
    class="grid md:w-full sm:col-12 md:p-2 lg:w-10 mt-3 gap-2"
    @submit.prevent="save"
  >
    <div class="flex flex-column md:col-6 lg:col-4">
      <label for="employee">Empleado</label>
      <AutoComplete
        v-model="selectedEmployee"
        :option-label="employeeLabel"
        :suggestions="filteredEmployees"
        @complete="searchEmployees"
        class="mt-2"
        :disabled="!isEditMode"
      >
        <template #option="slotProps">
          <div class="flex align-options-center">
            <div>
              {{ slotProps.option.code + ' - ' + slotProps.option.name }}
            </div>
          </div>
        </template>
      </AutoComplete>
    </div>
    <div class="flex flex-column sm:col-12 md:p-2 md:col-6 lg:col-4">
      <label for="name">Nombre<span class="text-red-300 ml-1">*</span></label>
      <InputText
        name="name"
        v-model="name"
        class="mt-2"
        :disabled="!isEditMode"
      />
      <small id="text-error" class="p-error">{{ nameError || '&nbsp;' }}</small>
    </div>
    <div class="flex flex-column sm:col-12 md:p-2 md:col-6 lg:col-4">
      <label for="email"
        >Correo electrónico<span class="text-red-300 ml-1">*</span></label
      >
      <InputText
        name="email"
        v-model="email"
        class="mt-2"
        :disabled="!isEditMode"
      />
      <small id="text-error" class="p-error">{{
        emailError || '&nbsp;'
      }}</small>
    </div>
    <div class="flex flex-column sm:col-12 md:p-2 md:col-6 lg:col-4">
      <label for="role"
        >Tipo de usuario<span class="text-red-300 ml-1">*</span></label
      >
      <Dropdown
        v-model="selectedRole"
        :options="roles"
        optionLabel="renamedLabel"
        placeholder="Seleccionar rol"
        class="mt-2"
        :disabled="!isEditMode"
      />
      <small id="text-error" class="p-error">{{ roleError || '&nbsp;' }}</small>
    </div>
    <div
      class="flex flex-row sm:col-12 md:p-2 md:col-6 lg:col-4 align-self-center"
      v-if="isRRHHAccessible"
    >
      <label for="isRRHH" class="mb-2">Habilitar acceso RRHH</label>
      <InputSwitch
        class="ml-2"
        id="isRRHH"
        v-model="isRRHH"
        :disabled="!isEditMode"
      />
    </div>
    <div
      class="flex flex-column sm:col-12 md:p-2 md:col-6 lg:col-4"
      v-if="isCompanySelectionVisible"
    >
      <label for="role">Compañias</label>
      <MultiSelectDialog
        label="Compañias"
        header-title="Seleccionar compañias"
        :options="userCompanies || []"
        v-model="selectedCompanies"
        :disabled="!isEditMode"
        show-key="formattedLabel"
        value-key="id"
        class="w-auto mt-2 mr-5"
        :required="false"
      />
    </div>
    <div
      class="flex flex-column sm:col-12 md:p-2 md:col-6 lg:col-4"
      v-if="isModuleSelectionVisible"
    >
      <label for="modules">Módulos</label>
      <MultiSelectDialog
        id="modules"
        label="Módulos"
        header-title="Seleccionar módulos"
        :options="adminModules || []"
        v-model="selectedModules"
        :disabled="!isEditMode"
        show-key="name"
        value-key="id"
        class="w-auto mt-2 mr-5"
        :required="false"
      />
    </div>
    <div
      class="flex flex-column sm:col-12 md:p-2 md:col-6 lg:col-4"
      v-if="userStatus === 'FORCE_CHANGE_PASSWORD'"
    >
      <label for="role">Reenviar contraseña temporal</label>
      <Button
        class="w-2 p-2 mt-2 bg-gray-300 border-gray-300 text-blue-600"
        icon="pi pi-sync"
        @click="onResendTemporaryPassword"
      />
    </div>
  </form>
  <Footer>
    <template #left>
      <AuditButton :record-ids="[userId]" :entities="['User']" />
    </template>
    <Button
      :label="isEditMode ? 'Cancelar' : 'Volver'"
      severity="secondary"
      @click="router.back()"
      class="mr-2 text-sm font-light"
    />
    <Button
      class="text-sm font-light"
      v-if="isEditMode"
      label="Guardar"
      @click="save"
      severity="primary"
    />
  </Footer>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { useUserForm } from '../composables/useUserForm'

  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import EditRecordButton from '@/shared/components/EditRecordButton.vue'
  import Footer from '@/shared/components/Footer.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import MultiSelectDialog from '@/shared/components/MultiSelectDialog.vue'

  const route = useRoute()
  const userId = computed(() =>
    route.params.username.toString().replace(/-/g, '.')
  )

  const {
    name,
    email,
    selectedRole,
    selectedEmployee,
    selectedCompanies,
    selectedModules,
    roles,
    isRRHH,
    adminModules,
    userCompanies,
    filteredEmployees,
    nameError,
    emailError,
    roleError,
    meta,
    isCompanySelectionVisible,
    isModuleSelectionVisible,
    isRRHHAccessible,
    save,
    searchEmployees,
    employeeLabel,
    router,
    isEditMode,
    toggleEditMode,
    userStatus,
    onResendTemporaryPassword,
  } = useUserForm(userId.value)
</script>

<style scoped>
  :deep(.p-inputtext) {
    width: 100%;
  }
</style>
